import { useEffect, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Card, CardBody, Row, Col, Image, Container, Breadcrumb, Alert, Button
} from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { MEDIA_BASE_URL } from "@/api/api";
import { Timeline, Statistic } from "antd";
import { FaDownload, FaThumbsUp, FaTimes } from "react-icons/fa";
import { MdPendingActions } from "react-icons/md";
import { FaImage } from "react-icons/fa6";
import { taskDetail } from "@/store/tasksApi/tasksThunk";
import formatDate from "../utils/formatDate";
import truncateChars from "../utils/cutWords";
import { HomeOutlined } from "@ant-design/icons";
import { getFileName, normalizeUrl } from "../utils/normalizeUrl";

const { Countdown } = Statistic;

const STATUS = {
  PENDING: "pending",
  IN_PROGRESS: "in_progress",
  SUBMITTED: "submitted",
  APPROVED: "approved",
  REJECTED: "rejected",
  PENDING_AFTER_REJECTION: "pending_after_rejection",
};

const STATUS_LABEL = {
  pending: "Yaratildi",
  in_progress: "Bajarilmoqda",
  submitted: "Topshirildi",
  approved: "Tasdiqlandi",
  rejected: "Rad etildi",
  pending_after_rejection: "Rad etilgan",
};

const STATUS_COLOR = {
  pending: "gray",
  in_progress: "blue",
  submitted: "#faad14",
  approved: "green",
  rejected: "red",
  pending_after_rejection: "red",
};

// 25-09-25 14:35 format
const formatShortDateTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  const dd = pad(d.getDate());
  const mm = pad(d.getMonth() + 1);
  const yy = String(d.getFullYear()).slice(-2);
  const hh = pad(d.getHours());
  const mi = pad(d.getMinutes());
  return `${dd}-${mm}-${yy} ${hh}:${mi}`;
};

const statusAlertMap = {
  [STATUS.SUBMITTED]: { variant: "warning", text: "Topshiriq ko‘rib chiqilmoqda.", icon: <MdPendingActions className="ms-2" /> },
  [STATUS.APPROVED]: { variant: "success", text: "Topshiriq tasdiqlangan.", icon: <FaThumbsUp className="ms-2" /> },
  [STATUS.REJECTED]: { variant: "danger", text: "Topshiriq rad etilgan.", icon: <FaTimes className="ms-2" /> },
};

const DAY = 24 * 60 * 60 * 1000;

const MyTaskDetail = () => {
  const dispatch = useDispatch();
  const { id } = useParams();
  const { tasksDetailData: task, loadingTaskDetail } = useSelector((s) => s.tasksApi);

  useEffect(() => {
    if (id) dispatch(taskDetail(id));
  }, [dispatch, id]);

  const isPending = task?.status === STATUS.PENDING;
  const isInProgress = task?.status === STATUS.IN_PROGRESS;
  const isTerminal = [STATUS.SUBMITTED, STATUS.APPROVED, STATUS.REJECTED].includes(task?.status);

  // Timer
  const deadlineTs = useMemo(() => (task?.deadline ? new Date(task.deadline).getTime() : null), [task?.deadline]);
  const remainingMs = useMemo(() => (deadlineTs ? deadlineTs - Date.now() : null), [deadlineTs]);

  const timerVariant = useMemo(() => {
    if (remainingMs == null) return "success";
    if (remainingMs <= 0) return "danger";
    if (remainingMs <= 1 * DAY) return "danger";
    if (remainingMs <= 3 * DAY) return "warning";
    return "success";
  }, [remainingMs]);

  const timerBoxClass =
    timerVariant === "danger"
      ? "bg-danger-subtle border border-danger text-danger"
      : timerVariant === "warning"
        ? "bg-warning-subtle border border-warning text-warning"
        : "bg-success-subtle border border-success text-success";

  // Timeline
  const rawHistory = Array.isArray(task?.status_history) ? task.status_history : [];
  const lastStatus = rawHistory.length ? rawHistory[rawHistory.length - 1].status : null;
  const timelineHistory = rawHistory.slice(1); // birinchi (pending) ko‘rsatilmaydi
  const pendingText = lastStatus === "approved" ? undefined : "Kutilmoqda";
  const timelineItems = timelineHistory.map((h) => {
    const title = `${STATUS_LABEL[h.status] || h.status}${h.reason ? " — " + h.reason : ""}`;
    return {
      color: STATUS_COLOR[h.status] || "gray",
      children: (
        <div>
          <div className="d-flex flex-column justify-content-start">
            <strong>{title}</strong>
            <small className="text-muted">{formatShortDateTime(h.timestamp)}</small>
          </div>
        </div>
      ),
    };
  });

  // HERO image (terminalda attachments-dagi birinchi rasm, aks holda task.image)
  const firstAttachmentImage = isTerminal
    ? (task?.attachments || []).find((a) => a.image_url)?.image_url
    : null;

  const formatUrl = (url) => {
    if (!url) return null;
    return url.replace("/apis", ""); // faqat birinchi uchragan /apis ni olib tashlaydi
  };

  const heroImageSrc = firstAttachmentImage
    ? (firstAttachmentImage.startsWith("http")
      ? formatUrl(firstAttachmentImage)
      : MEDIA_BASE_URL + firstAttachmentImage
    )
    : (task?.image ? formatUrl(MEDIA_BASE_URL + task.image) : null);

  return (
    <Container fluid>
      <Breadcrumb className="py-2">
        <Breadcrumb.Item linkAs={Link} linkProps={{ to: "/" }}> <HomeOutlined /></Breadcrumb.Item>
        <Breadcrumb.Item linkAs={Link} linkProps={{ to: "/my-tasks/list" }}>Men yaratganlar</Breadcrumb.Item>
        <Breadcrumb.Item active>{truncateChars(task?.title || "", 50)}</Breadcrumb.Item>
      </Breadcrumb>

      <Row className="justify-content-center">
        <Col>
          <Card className="border-0 rounded-3 shadow-sm">
            <CardBody>

              {loadingTaskDetail && (
                <div className="py-5 text-center text-muted">Yuklanmoqda…</div>
              )}

              {!loadingTaskDetail && task && (
                <>
                  {/* ===== TOP TIMER (faqat readonly ko'rsatish) ===== */}
                  {deadlineTs && (isPending || isInProgress) && (
                    <div
                      className={`p-3 rounded-3 d-flex flex-wrap align-items-center justify-content-between mb-3 ${timerBoxClass}`}
                    >
                      <div className="me-3">
                        <div className="fw-semibold">Deadline gacha</div>
                        <small className="opacity-75">
                          {formatShortDateTime(task?.deadline)} gacha
                        </small>
                      </div>
                      {remainingMs > 0 ? (
                        <Countdown
                          value={deadlineTs}
                          format="DD [kun] HH:mm:ss"
                          valueStyle={{ fontWeight: 700, fontSize: 22 }}
                        />
                      ) : (
                        <div className="fw-bold" style={{ fontSize: 15 }}>
                          Muddati tugagan
                        </div>
                      )}
                    </div>
                  )}
                  {/* ===== /TOP TIMER ===== */}

                  {/* User */}
                  <div className="d-flex align-items-center mb-4 p-3 rounded border-primary">
                    <Image
                      src={task?.approver?.picture || "https://via.placeholder.com/60"}
                      roundedCircle width={50} height={50}
                      className="me-3 shadow-sm object-fit-cover"
                    />
                    <div>
                      <h6 className="mb-0 fw-bold">
                        {task?.approver?.first_name} {task?.approver?.surname}{" "}
                      </h6>
                      <small className="text-muted">Ijroni ta’minlash uchun mas’ul</small>
                    </div>
                  </div>

                  {/* ==== HERO SECTION ==== */}
                  <h4 className="mb-3 text-primary">{task?.title}</h4>

                  <Row className="g-4 align-items-start mb-4">
                    {/* Left: description */}
                    <Col md={6}>
                      <div className="p-3 border rounded">
                        <h6 className="text-muted mb-2">Tavsif</h6>
                        <p className="mb-3">{task?.description || "—"}</p>
                      </div>
                    </Col>

                    {/* Right: image + meta */}
                    <Col md={6}>
                      <div className="ratio ratio-16x9 border rounded overflow-hidden">
                        {heroImageSrc ? (
                          <Image
                            src={heroImageSrc}
                            alt="Task image"
                            className="w-100 h-100"
                            style={{ objectFit: "cover" }}
                          />
                        ) : (
                          <div className="d-flex align-items-center justify-content-center w-100 h-100 bg-body-tertiary text-muted">
                            <FaImage className="me-2" /> Rasm mavjud emas
                          </div>
                        )}
                      </div>

                      {/* Root file (faqat bor bo'lsa ko'rsatamiz) */}
                      {task?.file && (
                        <div className="mt-3">
                          <a
                            className="btn btn-outline-secondary"
                            href={MEDIA_BASE_URL + task.file}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ textTransform: "none" }}
                          >
                            <div className="d-flex align-items-center gap-2">
                              <FaDownload />
                              <span>Faylni ochish</span>
                            </div>
                          </a>
                        </div>
                      )}

                      <div className="small mt-3">
                        <div className="mb-1"><strong>Yaratilgan:</strong> {formatDate(task?.created_at)}</div>
                        <div><strong>Muddati:</strong> {formatDate(task?.deadline)}</div>
                      </div>
                    </Col>
                  </Row>
                  {/* ==== /HERO SECTION ==== */}

                  <div className="row">
                    {/* Timeline */}
                    {timelineItems.length > 0 && (
                      <div className="mb-4 col-lg-4">
                        <h5 className="mt-4 mb-3">Holatlar tarixi</h5>
                        <Timeline pending={pendingText} items={timelineItems} />
                      </div>
                    )}

                    {/* Terminal holatlarda attachments */}
                    {Boolean(task?.attachments?.length) && (
                      <div className="mb-4 col-lg-8">
                        <h5 className="mb-2">Biriktirilgan fayllar</h5>
                        <Row className="g-3">
                          {task.attachments.map((att) => (
                            <Col md={6} key={att.id}>
                              {att.image_url && (
                                <div className="mb-2">
                                  <a
                                    className="btn btn-outline-secondary"
                                    href={MEDIA_BASE_URL + att.image_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ textTransform: "none" }}
                                  >
                                    <div className="d-flex align-items-center gap-2">
                                      <FaDownload />
                                      <span>{getFileName(att.image_url)}</span>
                                    </div>
                                  </a>
                                </div>
                              )}
                              {att.file_url && (
                                <a
                                  className="btn btn-outline-secondary"
                                  href={MEDIA_BASE_URL + att.file_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{ textTransform: "none" }}
                                >
                                  <div className="d-flex align-items-center gap-2">
                                    <FaDownload />
                                    <span>{getFileName(att.file_url)}</span>
                                  </div>
                                </a>
                              )}
                            </Col>
                          ))}
                        </Row>
                      </div>
                    )}
                  </div>

                  {/* Steps — READONLY */}
                  <h5 className="mt-4">Vazifalar</h5>
                  <ul className="list-group mb-3">
                    {task?.steps?.map((step) => (
                      <li
                        key={step.id}
                        className={`list-group-item d-flex justify-content-between align-items-center ${step.is_completed ? "list-group-item-success" : ""}`}
                      >
                        <div className="d-flex align-items-center gap-2">
                          {/* readonly checkbox (disabled) */}
                          <input
                            type="checkbox"
                            checked={step.is_completed}
                            readOnly
                            disabled
                          />
                          <span style={{ textDecoration: step.is_completed ? "none" : "none" }}>
                            {step.title}
                          </span>
                        </div>
                      </li>
                    ))}
                    {(!task?.steps || task.steps.length === 0) && (
                      <li className="list-group-item text-muted">Hozircha step qo‘shilmagan</li>
                    )}
                  </ul>

                  {/* Terminal status alert (faqat ko‘rsatish) */}
                  {isTerminal && (
                    <Alert
                      variant={statusAlertMap[task.status].variant}
                      className="d-flex align-items-center gap-2 justify-content-center"
                    >
                      {statusAlertMap[task.status].icon} {statusAlertMap[task.status].text}
                    </Alert>
                  )}
                </>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default MyTaskDetail;
